import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useSlotGroup, EditableSlotGroup, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import { toParagraph } from '../utils/format';
import { useRawProperty } from '../utils/hooks';
import { PATHS, tourCta, useSiteFlags } from '../utils/site';

interface AhaBandProps {
  tokenPrefix?: string;
  title?: string;
  lead?: string;
  /** "prompt": the gallery's band, which drops its lead on phones (Paper 08 mobile). */
  variant?: 'default' | 'prompt';
}

const tourCtaDef: SlotGroupDefinition = {
  label: 'Tour Button',
  description: 'Without a self-guided tour URL on the property, the default button becomes Contact Us.',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Book Self-Guided Tour' },
    url: { label: 'URL', type: 'url', defaultValue: '/schedule-a-tour' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
    vibesite_event: { label: 'Tracked Event', type: 'event', defaultValue: 'self_guided_tour_clicked' },
  },
};

const plansCtaDef: SlotGroupDefinition = {
  label: 'Floor Plans Button',
  fields: {
    label: { label: 'Button Text', type: 'text', defaultValue: 'Explore Floor Plans' },
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    _target: { label: 'Target', type: 'text', defaultValue: '_self' },
  },
};

/** One slim band per learn page, after its strongest visual content. */
export function AhaBand({ tokenPrefix, title, lead, variant = 'default' }: AhaBandProps) {
  const prefix = tokenPrefix || 'ahaband';
  const titlePath = `${prefix}.title`;
  const leadPath = `${prefix}.lead`;
  const tourPath = `${prefix}.tour_cta`;
  const plansPath = `${prefix}.plans_cta`;

  const p = useRawProperty();
  const flags = useSiteFlags();
  const tour = useSlotGroup(tourPath, tourCtaDef);
  const plans = useSlotGroup(plansPath, plansCtaDef);
  const tourLink = tourCta(tour, flags.selfTourUrl);

  const preLeasing = !((p.floorplans as unknown[]) || []).length && !((p.photo_gallery as unknown[]) || []).length;
  if (preLeasing) return null;

  const prompt = variant === 'prompt';

  return (
    <section
      className={`flex items-center justify-between gap-12 bg-primary px-[--gutter] py-10 text-primary-foreground [&_.btn-on-image-ghost]:border-primary-foreground [&_.btn-on-image-ghost]:text-primary-foreground [&_a:focus-visible]:outline-[--q-accent-on-primary] max-[767px]:flex-col max-[767px]:items-stretch ${prompt ? 'max-[767px]:gap-5' : 'max-[767px]:gap-4 max-[767px]:py-8'}`}
    >
      <div className="flex flex-col gap-1.5">
        <RichTextContent
          path={titlePath}
          as="h2"
          className={`m-0 font-heading text-[30px] font-light leading-9 ${prompt ? 'max-[767px]:text-[26px] max-[767px]:leading-8' : 'max-[767px]:text-[28px] max-[767px]:leading-[34px]'}`}
          defaultValue={toParagraph(title)}
        />
        <RichTextContent
          path={leadPath}
          as="p"
          className={`m-0 text-[15px] leading-[22px] opacity-[.85]${prompt ? ' max-[767px]:hidden' : ''}`}
          defaultValue={toParagraph(lead)}
        />
      </div>
      <div className="flex shrink-0 gap-3 max-[767px]:flex-col max-[767px]:gap-2.5">
        <EditableSlotGroup groupPath={tourPath} definition={tourCtaDef}>
          <a
            className="btn btn-on-image"
            href={tourLink.href}
            target={tourLink.isTour ? tour._target || '_self' : '_self'}
            data-vibesite-event={tourLink.isTour ? tour.vibesite_event || undefined : undefined}
          >
            {tourLink.label}
          </a>
        </EditableSlotGroup>
        <EditableSlotGroup groupPath={plansPath} definition={plansCtaDef}>
          <a className="btn btn-on-image-ghost" href={plans.url || PATHS.floorPlans} target={plans._target || '_self'}>
            {plans.label || 'Explore Floor Plans'} <span className="btn__arrow">→</span>
          </a>
        </EditableSlotGroup>
      </div>

      <EditableToken path="ahaband.title" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableToken path="ahaband.lead" as="span" className="hidden" style={{ display: 'none' }}>
        {''}
      </EditableToken>
      <EditableSlotGroup groupPath="ahaband.tour_cta" definition={tourCtaDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
      <EditableSlotGroup groupPath="ahaband.plans_cta" definition={plansCtaDef} className="hidden" style={{ display: 'none' }}>
        <span />
      </EditableSlotGroup>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'AhaBand',
  displayName: 'Aha Band',
  tokens: {
    'ahaband.title': { label: 'Title', type: 'richtext', defaultValue: '<p>Picture yourself here?</p>' },
    'ahaband.lead': { label: 'Lead', type: 'richtext', defaultValue: '<p>Book a self-guided tour and see the community on your own schedule.</p>' },
  },
  slotGroups: {
    'ahaband.tour_cta': tourCtaDef,
    'ahaband.plans_cta': plansCtaDef,
  },
};

(AhaBand as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default AhaBand;
